import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import axios from 'axios';
import Grid from '@mui/material/Grid2';
import InnerLayout from '../../layouts/InnerLayout';
import { useNavigate } from 'react-router-dom';

const RegistrationForm = () => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    highest_qualification: '',
    year_of_graduation: '',
    college_name: '',
    member_of_kdl: null,
    available: '',
    linkedin_url: '',
    resume: null,
    confirmation: 0, // Initialize as 0 (unchecked)
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
  
    // Email validation
    if (!formData.email.includes('@')) {
      newErrors.email = 'Please enter a valid email address containing @';
    }
  
    // Phone validation (numbers only)
    if (!/^\d*$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must contain only numbers';
    }
  
    // Resume validation (PDF only)
    if (formData.resume && !formData.resume.name.toLowerCase().endsWith('.pdf')) {
      newErrors.resume = 'Resume must be in PDF format';
    }
  
    // Confirmation validation
    if (formData.confirmation !== 1) {
      newErrors.confirmation = 'You must confirm the information provided';
    }
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckboxChange = (e, field) => {
    const { name, checked } = e.target;
    setFormData((prevData) => {
        const updatedArray = checked
            ? [...prevData[field], name]
            : prevData[field].filter((item) => item !== name);
        return { ...prevData, [field]: updatedArray };
    });
};

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
    // Clear error for the field being edited
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const navigate = useNavigate();

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, resume: file });
    // Validate resume format
    if (file && !file.name.toLowerCase().endsWith('.pdf')) {
      setErrors((prev) => ({
        ...prev,
        resume: 'Resume must be in PDF format',
      }));
    } else {
      setErrors((prev) => ({ ...prev, resume: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submissionData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      submissionData.append(key, value);
    });

    // Debug: log form data
    for (let [key, value] of submissionData.entries()) {
      console.log(`${key}:`, value);
    }

    try {
      await axios.post('https://test.kalamlabs.com/api/submit-training-form', submissionData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      navigate('/registration-success');
    } catch (err) {
      console.error(err);
      alert('Error submitting form');
    }
  };

  return (
    <InnerLayout>
      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 700, mx: 'auto', mt: 5, p: 3, boxShadow: 3, borderRadius: 2 }}>
        <Typography variant="h5" gutterBottom  sx={{ mt: 2, textAlign: 'center' }}>
          Cognizant Training Registration Form
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs:12,md:6 }}>
            <TextField 
              name="first_name" 
              label="First Name" 
              fullWidth 
              required 
              onChange={handleChange} 
              error={!!errors.first_name}
              helperText={errors.first_name}
            />
          </Grid>
          <Grid size={{ xs:12,md:6 }}>
            <TextField 
              name="last_name" 
              label="Last Name" 
              fullWidth 
              required 
              onChange={handleChange}
              error={!!errors.last_name}
              helperText={errors.last_name}
            />
          </Grid>
          <Grid size={{ xs:12,md:6 }}>
            <TextField 
              name="email" 
              label="Email" 
              type="email" 
              fullWidth 
              required 
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
          <Grid size={{ xs:12,md:6 }}>
            <TextField 
              name="phone" 
              label="Phone" 
              fullWidth 
              required 
              onChange={handleChange}
              error={!!errors.phone}
              helperText={errors.phone}
            />
          </Grid>
          <Grid size={{ xs:12,md:6 }}>
            <TextField 
              name="highest_qualification" 
              label="Qualification" 
              fullWidth 
              required
              onChange={handleChange}
              error={!!errors.highest_qualification}
              helperText={errors.highest_qualification}
            />
          </Grid>
          <Grid size={{ xs:12,md:6 }}>
            <TextField 
              name="year_of_graduation" 
              label="Year of Graduation" 
              type="number" 
              fullWidth 
              required
              onChange={handleChange}
              error={!!errors.year_of_graduation}
              helperText={errors.year_of_graduation}
            />
          </Grid>
          <Grid size={{ xs:12,md:6 }}>
            <TextField 
              name="college_name" 
              label="College Name" 
              fullWidth 
              required
              onChange={handleChange}
              error={!!errors.college_name}
              helperText={errors.college_name}
            />
          </Grid>
          <Grid size={{ xs:12,md:6 }}>
            <TextField
              select
              name="member_of_kdl"
              label="Member of KDL or Vizag Volunteers?"
              fullWidth
              required
              value={formData.member_of_kdl ?? ''}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  member_of_kdl: e.target.value === '' ? null : Number(e.target.value),
                }))
              }
              error={!!errors.member_of_kdl}
              helperText={errors.member_of_kdl}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value={1}>Yes</MenuItem>
              <MenuItem value={0}>No</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs:12,md:6 }}>
            <TextField
              select
              name="available"
              label="Are You Available Atleast Three hours a day?"
              fullWidth
              required
              value={formData.available}
              onChange={handleChange}
              error={!!errors.available}
              helperText={errors.available}
            >
              <MenuItem value="">Select</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs:12,md:6 }}>
            <TextField 
              name="linkedin_url" 
              label="LinkedIn URL" 
              fullWidth 
              required
              onChange={handleChange}
              error={!!errors.linkedin_url}
              helperText={errors.linkedin_url}
            />
          </Grid>
          <Grid size={{ md:12 }}>
            <input
              accept="application/pdf"
              style={{ display: 'none' }}
              id="resume-upload"
              type="file"
              required
              onChange={handleFileChange}
            />
            <label htmlFor="resume-upload">
              <Button variant="contained" color="primary" component="span">
                Upload Resume
              </Button>
              {formData.resume && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {formData.resume.name}
                </Typography>
              )}
            </label>
            {errors.resume && (
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                {errors.resume}
              </Typography>
            )}
          </Grid>
          <Grid item size={{ md: 12 }}>
  <FormControlLabel
    control={
      <Checkbox
        name="confirmation"
        required
        checked={formData.confirmation === 1}
        onChange={(e) =>
          setFormData((prev) => ({
            ...prev,
            confirmation: e.target.checked ? 1 : 0,
          }))
        }
      />
    }
    label="I hereby confirm that all the information provided above is true. I commit to responsibly fulfilling the deliverables assigned to me"
  />
  {errors.confirmation && (
    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
      {errors.confirmation}
    </Typography>
  )}
</Grid>
          <Grid size={{ md:12 }} sx={{ mt: 2 }}>
            <Button variant="contained" color="primary" type="submit" >
              Submit
            </Button>
          </Grid>
        </Grid>
      </Box>
    </InnerLayout>
  );
};

export default RegistrationForm;